import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import { Sparkles, ArrowRight, CreditCard, Eye, EyeOff, Loader2 } from 'lucide-react';

export default function AuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // P0 FIX: Clear any stale service workers before login attempt
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          console.info('[AUTH] Unregistered stale service worker');
        }
      }
    } catch (swError) {
      console.warn('[AUTH] Service worker cleanup failed:', swError);
    }
    
    try {
      console.info('[AUTH] Attempting login with POST method...');
      const response = await apiClient.post('/auth/login', loginData);
      console.info('[AUTH] Login successful');
      login(response.data.token, response.data.user, response.data.default_tenant_id);
      toast.success('Connexion réussie !');
      navigate('/dashboard');
    } catch (error) {
      console.error('[AUTH] Login failed:', error.response?.data || error.message);
      
      // P0 FIX: Handle specific error types with better messages
      const errorData = error.response?.data;
      const errorCode = errorData?.error || errorData?.error_code;
      const errorMessage = errorData?.message || errorData?.detail;
      
      if (errorCode === 'HTTP_405') {
        // Method not allowed - likely a cache/service worker issue
        toast.error('Erreur de connexion. Veuillez vider le cache de votre navigateur et réessayer.');
        console.error('[AUTH] 405 Error - Possible stale cache. Error details:', errorData);
      } else if (errorCode === 'INVALID_CREDENTIALS') {
        toast.error('Email ou mot de passe incorrect');
      } else {
        toast.error(errorMessage || 'Échec de la connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1762433813475-e6b761cc23d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMHNvZnQlMjBwYXN0ZWwlMjBjb2xvcnN8ZW58MHx8fHwxNzY1OTAxMTkzfDA&ixlib=rb-4.1.0&q=85)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 mb-4 hover:opacity-80">
            <Sparkles className="w-10 h-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold text-foreground">AI Marketing Engine</h1>
          </button>
          <p className="text-muted-foreground">Automatisez votre marketing 24/7 en toute confiance</p>
        </div>

        <Card className="backdrop-blur-xl bg-white/90 border-white/20 shadow-2xl">
          <CardHeader>
            <CardTitle className="font-heading">Connexion</CardTitle>
            <CardDescription>Connectez-vous à votre compte</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit-button">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>

            {/* Signup CTA - Redirect to Pricing */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">Pas encore de compte ?</span>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-blue-800 font-medium mb-1">
                    Un abonnement est requis pour créer un compte
                  </p>
                  <p className="text-xs text-blue-600 mb-3">
                    Choisissez votre forfait et votre compte sera créé automatiquement après le paiement.
                  </p>
                  <Button 
                    variant="default" 
                    className="w-full"
                    onClick={() => navigate('/pricing')}
                  >
                    Voir les tarifs
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <a href="/privacy-policy" className="hover:underline">Confidentialité</a>
          {' • '}
          <a href="/terms" className="hover:underline">CGU</a>
        </div>
      </div>
    </div>
  );
}
